'use strict';

var FirebaseExt = require('./FirebaseExt');
var FirebaseObject = require('./FirebaseObject');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('FirebaseIndirectArray');

function buildRefString(srcRef, destRefOrFunc) {
    var refString = srcRef === null ? 'null' : srcRef.toString();

    if (typeof destRefOrFunc !== 'undefined' && destRefOrFunc !== null) {
        if (typeof destRefOrFunc === 'function') {
            refString += ' / ' + destRefOrFunc.name;
        } else {
            refString += ' / ' + destRefOrFunc.toString();
        }
    }

    return refString;
}

function FirebaseIndirectArray(srcRef, destRefOrFunc) {
    var refString = buildRefString(srcRef, destRefOrFunc);

    this.$srcRef = srcRef;
    this.$destRef = null;
    this.$destFunc = null;
    this.$isDirect = false;
    this.$refString = refString;
    this.$internalIsLoaded = false;
    this.$loadedPromises = [];
    this.$childAddedListeners = [];
    this.$childChangedListeners = [];
    this.$childRemovedListeners = [];
    this.$destroyListeners = [];
    this.$items = [];

    if (typeof destRefOrFunc === 'undefined') {
        this.$isDirect = true;
    } else {
        if (typeof destRefOrFunc === 'function') {
            this.$destFunc = destRefOrFunc;
        } else {
            this.$destRef = destRefOrFunc;
        }

        this.$initialItemsLoaded = {};
    }

    if (KayleeConnectionState.isWorkingOnline() && srcRef !== null) {
        this.$srcRef.on('child_added', this.$childAddedCb, this.$cancelledCb, this);
        this.$srcRef.on('child_removed', this.$childRemovedCb, this);
        this.$srcRef.once('value', this.$valueCb, this);
    }

    // Array.isArray will not work on objects which extend the Array
    // class. So instead of extending the Array class, we just return an
    // actual array, but we extend the array by binding the Companies
    // prototype methods to the array instance.
    //
    // I borrowed this trick from AngularFire.
    //
    // AngularFire is released under the MIT license, so we are free to use
    // this trick without publishing our code.

    KayleeUtil.bindPrototypeMethods(FirebaseIndirectArray.prototype, this,
        this.$items);
    log.debug('created ' + refString);
    return this.$items;
}

FirebaseIndirectArray.$fromJson = function(data, name, reqProps, exclProps, create) {
    reqProps = reqProps.concat([ '$items', '$internalIsLoaded' ]);
    exclProps = exclProps.concat([ '$nodeType', '$items' ]);

    var arr = FirebaseObject.$intFromJson(data, name, reqProps, exclProps, function(data) {
        // FirebaseIndirectArrays return the $items. array, but we want to
        // augment the underlying object.
        return create(data).$self;
    });

    for (var i = 0; i < data.$items.length; i++) {
        var item = KayleeCache.get(data.$items[i]);

        if (item === null) {
            log.error('fromJson: ' + name + ': ' + data.$items[i] +
                ' not present in cache');
            continue;
        }

        arr.$items.push(item);
    }

    return arr.$items;
};

FirebaseIndirectArray.prototype.$toJson = function(preserveProps, addData) {
    preserveProps = preserveProps.concat([ '$nodeType', '$internalIsLoaded' ]);

    if (typeof addData === 'undefined' || addData === null) addData = {};
    addData.$items = [];

    for (var i = 0; i < this.$items.length; i++) {
        addData.$items.push(this.$items[i].$cacheKey);
    }

    return FirebaseObject.$intToJson(this, preserveProps, addData);
};

FirebaseIndirectArray.prototype.$setSrcRef = function(srcRef) {
    if (this.$srcRef === null && srcRef === null) return;
    if (this.$srcRef !== null && srcRef !== null &&
        this.$srcRef.toString() === srcRef.toString()) return;

    log.debug('updating srcRef from ' + this.$srcRef + ' to ' + srcRef + ' for ' + this);

    if (KayleeConnectionState.isWorkingOnline() && this.$srcRef !== null) {
        this.$srcRef.off('child_added', this.$childAddedCb, this);
        this.$srcRef.off('child_removed', this.$childRemovedCb, this);
    }

    for (var i = 0; i < this.$items.length; i++) {
        var item = this.$items[0];
        this.$items.splice(0, 1);
        this.$notifyChildRemovedListeners(item.$id);
    }

    this.$srcRef = srcRef;

    if (KayleeConnectionState.isWorkingOnline() && this.$srcRef !== null) {
        this.$srcRef.on('child_added', this.$childAddedCb, this.$cancelledCb, this);
        this.$srcRef.on('child_removed', this.$childRemovedCb, this);
        if (!this.$internalIsLoaded) {
            this.$srcRef.once('value', this.$valueCb, this);
        }
    }

    this.$refString = buildRefString(srcRef, this.$destRef || this.$destFunc);
    FirebaseExt.update();
};

FirebaseIndirectArray.prototype.$destroy = function(error) {
    log.debug('destroying ' + this.$refString);

    this.$notifyDestroyListeners();

    this.$srcRef.off('child_added', this.$childAddedCb, this);
    this.$srcRef.off('child_removed', this.$childRemovedCb, this);

    for (var i = 0, ii = this.$items.length; i < ii; i++) {
        if (KayleeConnectionState.isWorkingOnline() &&
            this.$childChangedListeners.length > 0) {
            this.$items[i].$offChange(this.$childChangedCb, this);
        }

        this.$notifyChildRemovedListeners(this.$items[i].$id);
    }

    this.$internalIsLoaded = false;
    if (typeof error === 'undefined') {
        this.$rejectLoadedPromises(new Error('array destroyed'));
    }
    else {
        this.$rejectLoadedPromises(error);
    }
    this.$childAddedListeners.length = 0;
    this.$childChangedListeners.length = 0;
    this.$childRemovedListeners.length = 0;
    this.$destroyListeners.length = 0;
    this.$items.length = 0;
    FirebaseExt.update();
};

FirebaseIndirectArray.prototype.$childAddedCb = function(snap) {
    var self = this;
    var child;

    if (this.$isDirect) {
        child = new FirebaseObject(this.$srcRef.child(snap.key));
    } else if (this.$destRef !== null) {
        child = new FirebaseObject(this.$destRef.child(snap.key));
    } else {
        child = this.$destFunc(snap.key);
    }

    child.$loaded().then(function(resolvedChild) {
        // Insert into array sorted by key.
        // We can't rely on using an orderByKey() on the reference because of
        // the indirect nature of the implementation. The keys in srcRef would
        // come in order, but when the corresponding children are loaded may
        // not happen in the same order. Thus, we need to sort here.

        var i = self.$items.length - 1;

        for (; i >= 0; i--) {
            if (self.$items[i].$id <= child.$id) break;
        }

        self.$items.splice(i + 1, 0, child);

        // Do housekeeping for $loaded promises.

        if (!self.$internalIsLoaded && !self.$isDirect) {
            self.$initialItemsLoaded[child.$id] = true;
            var loaded = true;

            for (var key in self.$initialItemsLoaded) {
                if (!self.$initialItemsLoaded.hasOwnProperty(key)) continue;
                if (!self.$initialItemsLoaded[key]) {
                    loaded = false;
                    break;
                }
            }

            if (loaded) {
                self.$internalIsLoaded = true;
                self.$resolveLoadedPromises();
                self.$initialItemsLoaded = {};
            }
        }

        // Add watch function.

        if (self.$childChangedListeners.length > 0) {
            child.$onChange(self.$childChangedCb, self, false);
        }

        // Notify listeners.

        self.$notifyChildAddedListeners(snap.key);
        FirebaseExt.update();
    })
    .catch(function(err) {
        // This can happen if we get unathenticated in the middle of loading.
        // Another example is if a controller gets unpaired in the middle of
        // loading. We don't do anything, but we need to have this catch here
        // so we don't get errors about unhandled promises.

        log.debug('failed loading ' + child + ' for ' + self + ', error: ' + err);
    });
};

FirebaseIndirectArray.prototype.$childRemovedCb = function(snap) {
    var index = this.$indexFor(snap.key);

    if (index < 0) {
        log.error('no object with key ' + snap.key + ' in ' + this.$refString);
        return;
    }

    if (this.$childChangedListeners.length > 0) {
        this.$items[index].$offChange(this.$childChangedCb, this);
    }

    this.$items.splice(index, 1);
    this.$notifyChildRemovedListeners(snap.key);

    if (!this.$internalIsLoaded && !this.$isDirect) {
        delete this.$initialItemsLoaded[snap.key];
    }

    FirebaseExt.update();
};

FirebaseIndirectArray.prototype.$childChangedCb = function(child) {
    this.$notifyChildChangedListeners(child.$id);
};

FirebaseIndirectArray.prototype.$valueCb = function(snap) {
    if (this.$isDirect) {
        this.$internalIsLoaded = true;
        this.$resolveLoadedPromises();
    } else {
        var keys = snap.val();

        if (keys === null) {
            this.$internalIsLoaded = true;
            this.$resolveLoadedPromises();
            return;
        }

        for (var key in keys) {
            if (!keys.hasOwnProperty(key)) continue;
            this.$initialItemsLoaded[key] = false;
        }
    }
};

FirebaseIndirectArray.prototype.$cancelledCb = function(error) {
    //log.debug('$cancelledCb: ' + this.$refString + ', error: ' + error);
    this.$destroy(error);
};

FirebaseIndirectArray.prototype.$notifyChildAddedListeners = function(key) {
    this.$childAddedListeners.forEach(function(listener) {
        if (listener.context) {
            listener.callback.call(listener.context, key);
        } else {
            listener.callback(key);
        }
    });
};

FirebaseIndirectArray.prototype.$notifyChildChangedListeners = function(key) {
    this.$childChangedListeners.forEach(function(listener) {
        if (listener.context) {
            listener.callback.call(listener.context, key);
        } else {
            listener.callback(key);
        }
    });
};

FirebaseIndirectArray.prototype.$notifyChildRemovedListeners = function(key) {
    this.$childRemovedListeners.forEach(function(listener) {
        if (listener.context) {
            listener.callback.call(listener.context, key);
        } else {
            listener.callback(key);
        }
    });
};

FirebaseIndirectArray.prototype.$isLoaded = function() {
    return this.$internalIsLoaded;
};

FirebaseIndirectArray.prototype.$loaded = function() {
    if (this.$internalIsLoaded) return Promise.resolve(this.$items);

    var deferredPromise = {};
    deferredPromise.promise = new Promise(function(resolve, reject) {
        deferredPromise.resolve = resolve;
        deferredPromise.reject = reject;
    });

    this.$loadedPromises.push(deferredPromise);

    return deferredPromise.promise;
};

FirebaseIndirectArray.prototype.$resolveLoadedPromises = function() {
    for (var i = 0; i < this.$loadedPromises.length; i++) {
        this.$loadedPromises[i].resolve(this.$items);
    }

    this.$loadedPromises.length = 0;
};

FirebaseIndirectArray.prototype.$rejectLoadedPromises = function(reason) {
    for (var i = 0; i < this.$loadedPromises.length; i++) {
        this.$loadedPromises[i].reject(reason);
    }

    this.$loadedPromises.length = 0;
};

FirebaseIndirectArray.prototype.$onChildAdded = function(listener, context) {
    if (typeof context === 'undefined') context = null;
    this.$childAddedListeners.push({callback: listener, context: context});

    for (var i = 0; i < this.$items.length; i++) {
        if (context) {
            listener.call(context, this.$items[i].$id);
        } else {
            listener(this.$items[i].$id);
        }
    }
};

FirebaseIndirectArray.prototype.$offChildAdded = function(listener, context) {
    if (typeof context === 'undefined') context = null;
    for (var i = 0; i < this.$childAddedListeners.length; i++) {
        if (this.$childAddedListeners[i].callback === listener &&
            this.$childAddedListeners[i].context === context) {
            this.$childAddedListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseIndirectArray.prototype.$onChildChanged = function(listener, context) {
    if (typeof context === 'undefined') context = this;
    this.$childChangedListeners.push({callback: listener, context: context});

    if (this.$childChangedListeners.length === 1) {
        for (var i = 0; i < this.$items.length; i++) {
            this.$items[i].$onChange(this.$childChangedCb, this, false);
        }
    }
};

FirebaseIndirectArray.prototype.$offChildChanged = function(listener, context) {
    if (typeof context === 'undefined') context = this;
    var i;

    for (i = 0; i < this.$childChangedListeners.length; i++) {
        if (this.$childChangedListeners[i].callback === listener &&
            this.$childChangedListeners[i].context === context) {
            this.$childChangedListeners.splice(i, 1);
            break;
        }
    }

    if (this.$childChangedListeners.length === 0) {
        for (i = 0; i < this.$items.length; i++) {
            this.$items[i].$offChange(this.$childChangedCb, this);
        }
    }
};

FirebaseIndirectArray.prototype.$onChildRemoved = function(listener, context) {
    if (typeof context === 'undefined') context = null;
    this.$childRemovedListeners.push({callback: listener, context: context});
};

FirebaseIndirectArray.prototype.$offChildRemoved = function(listener, context) {
    if (typeof context === 'undefined') context = null;
    for (var i = 0; i < this.$childRemovedListeners.length; i++) {
        if (this.$childRemovedListeners[i].callback === listener &&
            this.$childRemovedListeners[i].context === context) {
            this.$childRemovedListeners.splice(i, 1);
            break;
        }
    }
};

FirebaseIndirectArray.prototype.$onDestroy = function(listener, context) {
    if (typeof context === 'undefined') context = null;
    this.$destroyListeners.push({callback: listener, context: context});
};

FirebaseIndirectArray.prototype.$offDestroy = function(listener, context) {
    if (typeof context === 'undefined') context = null;
    for (var i = 0; i < this.$destroyListeners.length; i++) {
        if (this.$destroyListeners[i].callback === listener &&
            this.$destroyListeners[i].context === context) {
            this.$destroyListeners.splice(i, 1);
        }
    }
};

FirebaseIndirectArray.prototype.$notifyDestroyListeners = function() {
    for (var i = 0; i < this.$destroyListeners.length; i++) {
        if (this.$destroyListeners[i].context) {
            this.$destroyListeners[i].callback.call(this.$destroyListeners[i].context);
        } else {
            this.$destroyListeners[i].callback();
        }
    }
};

FirebaseIndirectArray.prototype.$indexFor = function(key) {
    var items = this.$items;

    for (var i = 0, ii = items.length; i < ii; i++) {
        if (items[i].$id === key) return i;
    }

    return -1;
};

FirebaseIndirectArray.prototype.$recordAt = function(key) {
    var index = this.$indexFor(key);
    if (index < 0) return null;
    return this.$items[index];
};

FirebaseIndirectArray.prototype.$waitForChildAdded = function(key) {
    var self = this;
    var child = self.$recordAt(key);

    if (child !== null) {
        return Promise.resolve(child);
    }

    var resolveChildAddedPromise;
    var childAddedPromise = new Promise(function(resolve, reject) {
        resolveChildAddedPromise = resolve;
    });
    var listener = function(addedKey) {
        if (addedKey === key) {
            self.$offChildAdded(listener);
            resolveChildAddedPromise(self.$recordAt(key));
        }
    };

    self.$onChildAdded(listener);
    return childAddedPromise;
};

FirebaseIndirectArray.prototype.$waitForChildRemoved = function(key) {
    var self = this;
    var child = self.$recordAt(key);

    if (child === null) {
        return Promise.resolve();
    }

    var resolveChildRemovedPromise;
    var childRemovedPromise = new Promise(function(resolve, reject) {
        resolveChildRemovedPromise = resolve;
    });
    var listener = function(removedKey) {
        if (removedKey === key) {
            self.$offChildRemoved(listener);
            resolveChildRemovedPromise();
        }
    };

    self.$onChildRemoved(listener);
    return childRemovedPromise;
};

// This is a bit of a hack, added for SNTY-1306. It is only partially
// implemented to handle that specific case. A full solution would be more
// generally implemented in FirebaseObject, FirebaseArray,
// FirebaseIndirectArray, and possibly KayleeCache.

FirebaseIndirectArray.prototype.$add = function(obj, key) {
    if (KayleeConnectionState.isWorkingOnline()) {
        log.error('$add: This method must never be called while working online; it is a workaround for an offline issue.');
        return;
    }

    if (this.$destFunc === null) {
        log.error('$add: This workaround is only implemented for indirect arrays managed by a reference function.');
        return;
    }

    if (typeof key === 'undefined') key = KayleeBase.getNewKey();

    var snap = { key: key  };
    var item = this.$destFunc(snap.key);
    Object.assign(item, obj);
    item.$internalIsLoaded = true;
    this.$childAddedCb(snap);
    return snap.key;
};

module.exports = FirebaseIndirectArray;
